import { useEffect, useState } from "react";
import { Calculator, PackagePlus, FileUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, appApi, inr, formatApiErrorDetail } from "@/lib/api";

const EMPTY_DEL = { name: "", phone: "", address: "", city: "", state: "", pincode: "" };
const EMPTY_BOX = { units: "1", length: "", width: "", height: "" };
const SLAB_HEADS = ["≤50 kg", "≤100 kg", "≤300 kg", "≤500 kg", "≤1000 kg", "≤2000 kg", ">2000 kg"];

// app contract: boxes = [{units, length, width, height}] (only complete rows)
const payloadBoxes = (rows) =>
  rows
    .filter((b) => b.length && b.width && b.height)
    .map((b) => ({
      units: parseInt(b.units, 10) || 1,
      length: Number(b.length),
      width: Number(b.width),
      height: Number(b.height),
    }));

export const RateChart = ({ card }) => {
  const zones = card?.zones || {};
  const keys = Object.keys(zones);
  if (keys.length === 0) return null;
  return (
    <div className="border-2 border-obsidian bg-white overflow-x-auto" data-testid="ratecard-chart">
      <p className="px-5 py-4 border-b-2 border-obsidian text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">
        Your contracted zone rate chart
      </p>
      <table className="w-full text-sm min-w-[860px]">
        <thead>
          <tr className="border-b-2 border-obsidian text-left">
            <th className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Zone</th>
            <th className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Region</th>
            <th className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">First 500g</th>
            <th className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Add'l 1kg</th>
            {SLAB_HEADS.map((h, i) => (
              <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500" title={`Per-kg rate, ${h}`}>
                {h}/kg
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {keys.map((z) => (
            <tr key={z} className="border-b border-zinc-100 last:border-0" data-testid={`ratecard-zone-${z}`}>
              <td className="px-4 py-3 font-display font-black text-lg text-acid">{z}</td>
              <td className="px-4 py-3 text-xs text-zinc-600">{zones[z].label}</td>
              <td className="px-4 py-3 font-mono text-xs font-bold">{inr(zones[z].first_500)}</td>
              <td className="px-4 py-3 font-mono text-xs font-bold">{inr(zones[z].add_1kg)}</td>
              {(zones[z].slabs || []).map((s, i) => (
                <td key={i} className="px-4 py-3 font-mono text-xs">{inr(s)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-5 py-3 border-t border-zinc-100 text-[11px] font-mono text-zinc-400 uppercase tracking-widest">
        Volumetric divisor {card.divisor} · ODA {inr(card.oda_per_kg)}/kg · GST {card.gst_pct}% extra
      </p>
    </div>
  );
};

export default function RateCard() {
  const [card, setCard] = useState(null);
  const [form, setForm] = useState({ delivery_pincode: "", weight_kg: "" });
  const [boxes, setBoxes] = useState([{ ...EMPTY_BOX }]);
  const [quote, setQuote] = useState(null);
  const [courierId, setCourierId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bookingOpen, setBookingOpen] = useState(false);
  const [del, setDel] = useState(EMPTY_DEL);
  const [invNo, setInvNo] = useState("");
  const [invVal, setInvVal] = useState("");
  const [invFile, setInvFile] = useState(null);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    appApi.get("ratecard/me").then(setCard).catch(() => setCard({ enabled: false }));
  }, []);

  // upstream may omit `enabled` when a card exists — treat a present zone chart as active
  const cardActive = card ? card.enabled === true || Object.keys(card.card?.zones || {}).length > 0 : false;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setD = (k) => (e) => setDel((d) => ({ ...d, [k]: e.target.value }));

  const getQuote = async (e) => {
    e.preventDefault();
    setError("");
    setQuote(null);
    setCourierId("");
    setBookingOpen(false);
    const bx = payloadBoxes(boxes);
    if (bx.length === 0) {
      setError("Add at least one box with length, width and height.");
      return;
    }
    setLoading(true);
    try {
      const d = await appApi.post("ratecard/quote", {
        delivery_pincode: form.delivery_pincode,
        weight_kg: parseFloat(form.weight_kg),
        boxes: bx,
      });
      setQuote(d);
      const cs = d.couriers || [];
      if (cs.length > 0) setCourierId(cs[0].courier_id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const total = quote?.total;
  const couriers = quote?.couriers || [];
  const courierIds = couriers.map((c) => String(c.courier_id));

  const confirmBook = async (e) => {
    e.preventDefault();
    if (!courierId) {
      toast.error("Choose a courier partner above.");
      return;
    }
    if (!invFile) {
      toast.error("Attach the goods invoice photo. It's required to book.");
      return;
    }
    if (!window.confirm("Book this rate-card shipment? Your wallet will be debited at the quoted contract rate.")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", invFile);
      const up = await api.post("/app/ratecard/invoice-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const invoice_file_id = up.data.id || up.data.file_id;
      const payload = {
        courier_id: courierId,
        delivery_pincode: form.delivery_pincode,
        weight_kg: parseFloat(form.weight_kg),
        boxes: payloadBoxes(boxes),
        invoice_file_id,
        delivery: del,
        invoice_value: parseFloat(invVal) || 0,
        invoice_number: invNo.trim(),
        description: desc.trim(),
      };
      const d = await appApi.post("ratecard/book", payload);
      toast.success(`Rate-card booking placed${d.awb ? ` — AWB ${d.awb}` : ""}. Wallet debited at your contract rate.`);
      setBookingOpen(false);
      setQuote(null);
      setInvFile(null);
      setInvNo("");
      setInvVal("");
      setDesc("");
      setDel(EMPTY_DEL);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!card) return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="ratecard-loading">Loading rate card…</p>;

  return (
    <div data-testid="ratecard-page" className="space-y-6 max-w-6xl">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Contract Rates</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">My Rate Card</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Your negotiated contract rates — identical to the app{card.billing_name ? ` · billing: ${card.billing_name}` : ""}.
        </p>
      </div>

      {!cardActive ? (
        <div className="border-2 border-obsidian bg-white p-8" data-testid="ratecard-disabled">
          <p className="font-bold text-lg">Rate card not enabled on your account yet.</p>
          <p className="text-sm text-zinc-500 mt-2">ShipZilla admin can assign you a custom zone rate chart. Raise a ticket under Support to request one.</p>
        </div>
      ) : (
        <RateChart card={card.card || {}} />
      )}

      {cardActive && (
        <>
          <form onSubmit={getQuote} className="border-2 border-obsidian bg-white p-6 space-y-5" data-testid="ratecard-quote-form">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { k: "delivery_pincode", label: "Delivery pincode", ph: "110001", max: 6 },
                { k: "weight_kg", label: "Actual weight — total (kg)", ph: "10", type: "number", min: "0.1", step: "0.1" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{f.label}</label>
                  <input type={f.type || "text"} required min={f.min} step={f.step}
                    maxLength={f.max} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph} data-testid={`ratecard-${f.k}`}
                    className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
                </div>
              ))}
            </div>

            <div data-testid="ratecard-boxes">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Boxes (volumetric — divisor {card.card?.divisor || 5000})</label>
                <button type="button" onClick={() => setBoxes((b) => [...b, { ...EMPTY_BOX }])} data-testid="ratecard-box-add"
                  className="text-[11px] font-mono font-bold uppercase tracking-widest text-acid hover:text-obsidian transition-colors duration-150">
                  + Add box
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {boxes.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`ratecard-box-${i}`}>
                    <span className="text-xs font-mono text-zinc-400 w-12">Box {i + 1}</span>
                    {[
                      { k: "units", ph: "Units", w: "w-20" },
                      { k: "length", ph: "L (cm)", w: "w-24" },
                      { k: "width", ph: "W (cm)", w: "w-24" },
                      { k: "height", ph: "H (cm)", w: "w-24" },
                    ].map((f) => (
                      <input key={f.k} type="number" min="0" step="0.5" value={b[f.k]} placeholder={f.ph}
                        onChange={(e) => setBoxes((rows) => rows.map((r, j) => (j === i ? { ...r, [f.k]: e.target.value } : r)))}
                        data-testid={`ratecard-box-${i}-${f.k}`}
                        className={`${f.w} border-2 border-obsidian bg-paper px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian`} />
                    ))}
                    {boxes.length > 1 && (
                      <button type="button" onClick={() => setBoxes((rows) => rows.filter((_, j) => j !== i))} aria-label={`Remove box ${i + 1}`} data-testid={`ratecard-box-remove-${i}`}
                        className="p-2 border-2 border-obsidian text-red-600 hover:bg-red-50 transition-colors duration-150">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-zinc-400 mt-2">Chargeable weight = max(actual, volumetric) — same rule as the app.</p>
            </div>

            <button type="submit" disabled={loading} data-testid="ratecard-quote-submit"
              className="inline-flex items-center justify-center gap-2 bg-obsidian text-sky-300 font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#2563EB] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              <Calculator className="w-4 h-4" /> {loading ? "Quoting…" : "Get Card Quote"}
            </button>
          </form>

          {error && <div className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3" data-testid="ratecard-error">{error}</div>}

          {quote && (
            <div className="border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]" data-testid="ratecard-quote">
              <div className="px-6 py-4 border-b-2 border-obsidian bg-obsidian text-white flex items-center justify-between flex-wrap gap-3">
                <p className="font-bold text-sm">
                  Zone <span className="text-sky-300">{quote.zone}</span>{quote.zone_label ? ` · ${quote.zone_label}` : ""} · {form.delivery_pincode}
                </p>
                {total != null && <p className="font-display font-black text-2xl text-sky-300" data-testid="ratecard-quote-total">{inr(total)}</p>}
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                {[
                  ["Actual / volumetric", `${quote.actual_kg ?? "—"} kg / ${quote.volumetric_kg ?? "—"} kg`],
                  ["Chargeable weight", `${quote.chargeable_kg ?? "—"} kg`],
                  ["Weight charge", quote.weight_charge != null ? inr(quote.weight_charge) : "—"],
                  ["ODA", quote.oda != null ? inr(quote.oda) : "—"],
                  ["Subtotal", quote.subtotal != null ? inr(quote.subtotal) : "—"],
                  [`GST ${quote.gst_pct ?? ""}%`, quote.gst != null ? inr(quote.gst) : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-zinc-100 pb-2">
                    <span className="text-zinc-500 text-xs font-mono uppercase tracking-widest">{k}</span>
                    <span className="font-mono text-xs font-bold">{v}</span>
                  </div>
                ))}
                {quote.weight_charge_how && (
                  <p className="sm:col-span-2 text-[11px] font-mono text-zinc-400" data-testid="ratecard-quote-how">{quote.weight_charge_how}</p>
                )}
              </div>

              {couriers.length > 0 && (
                <div className="px-6 pb-6">
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 mb-3">Courier partner</p>
                  <div className="flex gap-3 flex-wrap" data-testid="ratecard-couriers">
                    {couriers.map((c, i) => {
                      const cid = String(c.courier_id);
                      return (
                        <button key={cid} type="button" onClick={() => setCourierId(c.courier_id)} data-testid={`ratecard-courier-${cid}`}
                          className={`px-5 py-3 border-2 border-obsidian text-xs font-bold uppercase tracking-widest transition-all duration-150 ${String(courierId) === cid ? "bg-obsidian text-sky-300" : "bg-white hover:bg-acid/20"}`}>
                          {c.courier_name || cid}
                          {c.rate != null && <span className="block font-mono text-[10px] mt-1 normal-case tracking-normal">{inr(c.rate)}{c.eta_days ? ` · ETA ${c.eta_days}` : ""}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="px-6 py-4 border-t-2 border-obsidian flex justify-end">
                <button onClick={() => { setDel((d) => ({ ...d, pincode: form.delivery_pincode })); setBookingOpen(true); }}
                  data-testid="ratecard-book-open"
                  className="inline-flex items-center gap-2 bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150">
                  <PackagePlus className="w-4 h-4" /> Book at card rate
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {bookingOpen && quote && (
        <form onSubmit={confirmBook} className="border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]" data-testid="ratecard-booking-form">
          <div className="flex items-center justify-between px-6 py-4 border-b-2 border-obsidian bg-obsidian text-white">
            <p className="font-bold text-sm">Booking at card rate{total != null ? ` · ${inr(total)}` : ""} · {form.delivery_pincode}</p>
            <button type="button" onClick={() => setBookingOpen(false)} aria-label="Close rate card booking" data-testid="ratecard-booking-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 grid sm:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Delivery (consignee)</p>
              {[
                { k: "name", ph: "Consignee name" }, { k: "phone", ph: "Phone (10 digits)", max: 10 },
                { k: "address", ph: "Full address" }, { k: "city", ph: "City" }, { k: "state", ph: "State" },
                { k: "pincode", ph: "Pincode", max: 6 },
              ].map((f) => (
                <input key={f.k} required value={del[f.k]} onChange={setD(f.k)} placeholder={f.ph} maxLength={f.max}
                  data-testid={`ratecard-delivery-${f.k}`}
                  className="w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              ))}
            </div>
            <div className="space-y-3">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Goods invoice</p>
              <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Goods description (e.g. Seeds — 10 boxes)" data-testid="ratecard-description"
                className="w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              <input required value={invNo} onChange={(e) => setInvNo(e.target.value)} placeholder="Invoice number" data-testid="ratecard-invoice-number"
                className="w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              <input type="number" min="0" step="0.01" value={invVal} onChange={(e) => setInvVal(e.target.value)} placeholder="Invoice value (₹)" data-testid="ratecard-invoice-value"
                className="w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              <label className="flex items-center gap-2 border-2 border-dashed border-obsidian bg-paper px-4 py-3 text-xs font-mono cursor-pointer hover:bg-acid/10" data-testid="ratecard-invoice-file-label">
                <FileUp className="w-4 h-4" /> {invFile ? invFile.name : "Attach goods invoice photo (required)"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="ratecard-invoice-file"
                  onChange={(e) => setInvFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <div className="px-6 py-4 border-t-2 border-obsidian flex items-center justify-between flex-wrap gap-3">
            <p className="font-mono text-xs text-zinc-500">Wallet will be debited <span className="font-bold text-white bg-acid px-2 py-0.5 border border-obsidian">{inr(total)}</span> at your contract rate</p>
            <button type="submit" disabled={busy} data-testid="ratecard-booking-submit"
              className="inline-flex items-center gap-2 bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              <PackagePlus className="w-4 h-4" /> {busy ? "Booking…" : "Confirm Booking"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
